import classNames from "classnames";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { StrapiImage, StrapiProduct, StrapiVideo } from "~/shared-types";
import { getFormattedImageAttributes } from "~/utils/getImageAttributes";
import { Swiper, SwiperRef, SwiperSlide } from "swiper/react";
import { A11y, Pagination, Zoom } from "swiper";
import "swiper/swiper.min.css";
import "swiper/css/a11y";
import "swiper/css/pagination";
import { getAspectRatioClass } from "~/utils/getAspectRatioClass";
import { getOfferDetails } from "~/utils/getOfferDetails";
import { Badge } from "../Backroom/Badge";

export type ProductGalleryProps = {
  product: StrapiProduct;
};
export type ThumbnailProps = {
  image: StrapiImage;
  isActive: boolean;
  onClick: () => void;
};

type VideoSlideProps = {
  video: StrapiVideo;
};

function VideoSlide({ video }: VideoSlideProps) {
  const strapiBase =
    process.env.NEXT_PUBLIC_STRAPI_BASE_URL || "https://strapi.butikdanmark.dk";
  const url = video.attributes.url?.startsWith("http")
    ? video.attributes.url
    : `${strapiBase}${video.attributes.url}`;

  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  const [muted, setMuted] = useState(true);

  return (
    <div className="relative">
      <video
        ref={videoRef}
        src={url}
        autoPlay
        muted={muted}
        loop
        playsInline
        className="w-full rounded-xl max-h-[740px]"
        style={{ objectFit: "contain" }}
      />
      <button
        onClick={() => setMuted((m) => !m)}
        className="absolute bottom-4 right-4 bg-black/50 text-white rounded-full p-2 hover:bg-black/70 transition"
        aria-label={muted ? "Slå lyd til" : "Slå lyd fra"}
      >
        {muted ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M12 6v12m-3.536-9.536a5 5 0 000 7.072M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
          </svg>
        )}
      </button>
    </div>
  );
}

function ProductGallery({ product }: ProductGalleryProps) {
  const { hasCurrentOffer, offerTitle } = getOfferDetails(product);
  const images = product.attributes.images.data;
  const video = product.attributes.video?.data;
  const { featuredImageOrientation } = product.attributes;

  if (!images || images.length === 0) return null;

  const swiperRef = useRef<SwiperRef>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  const goToSlide = (index: number) => {
    swiperRef.current?.swiper.slideTo(index);
  };

  useEffect(() => {
    const onSlideChange = () => {
      // istanbul ignore next
      setActiveSlide(swiperRef.current?.swiper.activeIndex ?? 0);
    };
    swiperRef.current?.swiper?.on("slideChange", onSlideChange);
    return () => {
      swiperRef.current?.swiper?.off("slideChange", onSlideChange);
    };
  }, []);

  return (
    <div className="mb-8 md:mb-20">
      <Swiper
        pagination
        zoom={true}
        autoHeight
        ref={swiperRef}
        modules={[A11y, Pagination, Zoom]}
      >
        {images.map((image, index) => (
          <SwiperSlide key={image.id}>
            <div className="relative 2xl:max-w-xl">
              <div className="swiper-zoom-container">
                <Image
                  {...getFormattedImageAttributes(image, "large")}
                  className={classNames(
                    getAspectRatioClass(
                      image,
                      index === 0 ? featuredImageOrientation : undefined
                    ),
                    "w-full md:mb-5 rounded-xl 2xl:max-w-xl object-cover max-h-[740px]"
                  )}
                />
                {hasCurrentOffer && (
                  <Badge color="yellow" className="absolute top-4 left-4">
                    {offerTitle}
                  </Badge>
                )}
              </div>
            </div>
          </SwiperSlide>
        ))}
        {video && (
          <SwiperSlide key={`video-${video.id}`}>
            <div className="relative 2xl:max-w-xl">
              <VideoSlide video={video} />
            </div>
          </SwiperSlide>
        )}
      </Swiper>
      <div className="hidden md:grid grid-cols-4 xl:grid-cols-5 gap-3 lg:gap-5 mt-3 lg:mt-5">
        {images.length <= 1 && !video
          ? null
          : images.map((image, index) => (
              <Thumbnail
                key={image.id}
                image={image}
                isActive={activeSlide === index}
                onClick={() => goToSlide(index)}
              />
            ))}
        {video && (
          <button
            className={classNames(
              activeSlide === images.length && "!ring-1 ring-primary-500 rounded-xl"
            )}
            onClick={() => goToSlide(images.length)}
          >
            <div className="aspect-square rounded-xl bg-secondary-200 flex items-center justify-center contrast-[.85] hover:contrast-100 transition">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-8 h-8 text-secondary-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </button>
        )}
      </div>
    </div>
  );
}

function Thumbnail({ image, isActive, onClick }: ThumbnailProps) {
  return (
    <button
      className={classNames(isActive && "!ring-1 ring-primary-500 rounded-xl")}
      onClick={onClick}
    >
      <Image
        {...getFormattedImageAttributes(image, "small", {
          altFallback: `Thumbnail for ${image.attributes.name}`,
        })}
        // Thumbnails are ~125px in size
        className={classNames(
          "aspect-square object-cover rounded-xl transition contrast-[.85] hover:contrast-100",
          isActive && "!contrast-100"
        )}
      />
    </button>
  );
}

export { ProductGallery };
